import * as z from 'zod';

export const GetDiscountRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the discount belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    discountId: z
      .string()
      .describe('ID of the discount to retrieve.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const GetDiscountResponse = z.intersection(
  z.object({
    _id: z.string().describe('Discount ID.').optional().nullable(),
    name: z.string().describe('Discount name.').optional().nullable(),
    description: z
      .string()
      .describe('Discount description.')
      .optional()
      .nullable(),
    active: z
      .boolean()
      .describe('Whether the discount is active. Defaults to `true`.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNSPECIFIED_TYPE', 'OFF_ITEM', 'OFF_ORDER'])
      .describe('Discount type.')
      .optional(),
    condition: z
      .object({
        fulfillmentTypes: z
          .array(
            z.enum([
              'UNSPECIFIED_FULFILLMENT_TYPE',
              'DELIVERY',
              'PICKUP_OR_DINE_IN',
            ])
          )
          .min(1)
          .max(2)
          .optional(),
        platforms: z
          .array(
            z.enum([
              'UNSPECIFIED_PLATFORM',
              'SITE',
              'MOBILE_SITE',
              'CALL_CENTER',
            ])
          )
          .min(1)
          .max(3)
          .optional(),
        availability: z
          .object({
            periods: z
              .array(
                z.object({
                  openDay: z
                    .enum([
                      'UNDEFINED',
                      'SUN',
                      'MON',
                      'TUE',
                      'WED',
                      'THU',
                      'FRI',
                      'SAT',
                    ])
                    .describe('Day of the week the period starts on.')
                    .optional(),
                  openTime: z
                    .string()
                    .describe(
                      'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                    )
                    .optional(),
                  closeDay: z
                    .enum([
                      'UNDEFINED',
                      'SUN',
                      'MON',
                      'TUE',
                      'WED',
                      'THU',
                      'FRI',
                      'SAT',
                    ])
                    .describe('Day of the week the period ends on.')
                    .optional(),
                  closeTime: z
                    .string()
                    .describe(
                      'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                    )
                    .optional(),
                })
              )
              .optional(),
            specialHourPeriods: z
              .array(
                z.object({
                  startDate: z
                    .string()
                    .describe(
                      'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  endDate: z
                    .string()
                    .describe(
                      'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  available: z
                    .boolean()
                    .describe(
                      'Whether the item is available during the exception. Defaults to `true`.'
                    )
                    .optional(),
                  eventName: z
                    .string()
                    .describe(
                      'Name of the special hour period. In the dashboard, the special hour period is called event.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
          })
          .describe('List of times when the discount is available.')
          .optional(),
        minOrderPrice: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
          })
          .describe('Minimum order price for the discount.')
          .optional(),
        coupon: z
          .object({
            applied: z
              .boolean()
              .describe(
                'Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.'
              )
              .optional()
              .nullable(),
            code: z.string().describe('Coupon code.').optional().nullable(),
          })
          .describe('Coupon associated with the discount.')
          .optional(),
      })
      .describe(
        'Discount condition.\nAll conditions must be met so that a customer can apply the discount.'
      )
      .optional(),
  }),
  z.intersection(
    z.xor([
      z.object({
        amount: z.never().optional(),
        percentage: z.never().optional(),
      }),
      z.object({
        percentage: z.never().optional(),
        amount: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
          })
          .describe('Discount amount.'),
      }),
      z.object({
        amount: z.never().optional(),
        percentage: z.string().describe('Discount percentage.'),
      }),
    ]),
    z.xor([
      z.object({
        sectionIds: z.never().optional(),
        itemIds: z.never().optional(),
      }),
      z.object({
        itemIds: z.never().optional(),
        sectionIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe('IDs of the sections the discount applies to.'),
      }),
      z.object({
        sectionIds: z.never().optional(),
        itemIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe(
            'IDs of the items the discount applies to. **Note:** The items must be of type `dish`.'
          ),
      }),
    ])
  )
);
export const UpdateDiscountRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the discount belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    discountId: z.string().describe('Discount ID.'),
  }),
  options: z
    .object({
      discount: z
        .intersection(
          z.object({
            _id: z.string().describe('Discount ID.').optional().nullable(),
            name: z.string().describe('Discount name.').optional().nullable(),
            description: z
              .string()
              .describe('Discount description.')
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe('Whether the discount is active. Defaults to `true`.')
              .optional()
              .nullable(),
            type: z
              .enum(['UNSPECIFIED_TYPE', 'OFF_ITEM', 'OFF_ORDER'])
              .optional(),
            condition: z
              .object({
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'CALL_CENTER',
                    ])
                  )
                  .min(1)
                  .max(3)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('List of times when the discount is available.')
                  .optional(),
                minOrderPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                  })
                  .describe('Minimum order price for the discount.')
                  .optional(),
                coupon: z
                  .object({
                    applied: z
                      .boolean()
                      .describe(
                        'Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.'
                      )
                      .optional()
                      .nullable(),
                    code: z
                      .string()
                      .describe('Coupon code.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Coupon associated with the discount.')
                  .optional(),
              })
              .describe(
                'Discount condition.\nAll conditions must be met so that a customer can apply the discount.'
              )
              .optional(),
          }),
          z.intersection(
            z.xor([
              z.object({
                amount: z.never().optional(),
                percentage: z.never().optional(),
              }),
              z.object({
                percentage: z.never().optional(),
                amount: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                  })
                  .describe('Discount amount.'),
              }),
              z.object({
                amount: z.never().optional(),
                percentage: z.string().describe('Discount percentage.'),
              }),
            ]),
            z.xor([
              z.object({
                sectionIds: z.never().optional(),
                itemIds: z.never().optional(),
              }),
              z.object({
                itemIds: z.never().optional(),
                sectionIds: z
                  .object({ values: z.array(z.string()).optional() })
                  .describe('IDs of the sections the discount applies to.'),
              }),
              z.object({
                sectionIds: z.never().optional(),
                itemIds: z
                  .object({ values: z.array(z.string()).optional() })
                  .describe(
                    'IDs of the items the discount applies to. **Note:** The items must be of type `dish`.'
                  ),
              }),
            ])
          )
        )
        .describe('Discount to update.')
        .optional(),
    })
    .optional(),
});
export const UpdateDiscountResponse = z.intersection(
  z.object({
    _id: z.string().describe('Discount ID.').optional().nullable(),
    name: z.string().describe('Discount name.').optional().nullable(),
    description: z
      .string()
      .describe('Discount description.')
      .optional()
      .nullable(),
    active: z
      .boolean()
      .describe('Whether the discount is active. Defaults to `true`.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNSPECIFIED_TYPE', 'OFF_ITEM', 'OFF_ORDER'])
      .describe('Discount type.')
      .optional(),
    condition: z
      .object({
        fulfillmentTypes: z
          .array(
            z.enum([
              'UNSPECIFIED_FULFILLMENT_TYPE',
              'DELIVERY',
              'PICKUP_OR_DINE_IN',
            ])
          )
          .min(1)
          .max(2)
          .optional(),
        platforms: z
          .array(
            z.enum([
              'UNSPECIFIED_PLATFORM',
              'SITE',
              'MOBILE_SITE',
              'CALL_CENTER',
            ])
          )
          .min(1)
          .max(3)
          .optional(),
        availability: z
          .object({
            periods: z
              .array(
                z.object({
                  openDay: z
                    .enum([
                      'UNDEFINED',
                      'SUN',
                      'MON',
                      'TUE',
                      'WED',
                      'THU',
                      'FRI',
                      'SAT',
                    ])
                    .describe('Day of the week the period starts on.')
                    .optional(),
                  openTime: z
                    .string()
                    .describe(
                      'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                    )
                    .optional(),
                  closeDay: z
                    .enum([
                      'UNDEFINED',
                      'SUN',
                      'MON',
                      'TUE',
                      'WED',
                      'THU',
                      'FRI',
                      'SAT',
                    ])
                    .describe('Day of the week the period ends on.')
                    .optional(),
                  closeTime: z
                    .string()
                    .describe(
                      'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                    )
                    .optional(),
                })
              )
              .optional(),
            specialHourPeriods: z
              .array(
                z.object({
                  startDate: z
                    .string()
                    .describe(
                      'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  endDate: z
                    .string()
                    .describe(
                      'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  available: z
                    .boolean()
                    .describe(
                      'Whether the item is available during the exception. Defaults to `true`.'
                    )
                    .optional(),
                  eventName: z
                    .string()
                    .describe(
                      'Name of the special hour period. In the dashboard, the special hour period is called event.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
          })
          .describe('List of times when the discount is available.')
          .optional(),
        minOrderPrice: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
          })
          .describe('Minimum order price for the discount.')
          .optional(),
        coupon: z
          .object({
            applied: z
              .boolean()
              .describe(
                'Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.'
              )
              .optional()
              .nullable(),
            code: z.string().describe('Coupon code.').optional().nullable(),
          })
          .describe('Coupon associated with the discount.')
          .optional(),
      })
      .describe(
        'Discount condition.\nAll conditions must be met so that a customer can apply the discount.'
      )
      .optional(),
  }),
  z.intersection(
    z.xor([
      z.object({
        amount: z.never().optional(),
        percentage: z.never().optional(),
      }),
      z.object({
        percentage: z.never().optional(),
        amount: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
          })
          .describe('Discount amount.'),
      }),
      z.object({
        amount: z.never().optional(),
        percentage: z.string().describe('Discount percentage.'),
      }),
    ]),
    z.xor([
      z.object({
        sectionIds: z.never().optional(),
        itemIds: z.never().optional(),
      }),
      z.object({
        itemIds: z.never().optional(),
        sectionIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe('IDs of the sections the discount applies to.'),
      }),
      z.object({
        sectionIds: z.never().optional(),
        itemIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe(
            'IDs of the items the discount applies to. **Note:** The items must be of type `dish`.'
          ),
      }),
    ])
  )
);
export const CreateDiscountRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the discount belongs to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      discount: z
        .intersection(
          z.object({
            _id: z.string().describe('Discount ID.').optional().nullable(),
            name: z.string().describe('Discount name.'),
            description: z
              .string()
              .describe('Discount description.')
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe('Whether the discount is active. Defaults to `true`.'),
            type: z
              .enum(['UNSPECIFIED_TYPE', 'OFF_ITEM', 'OFF_ORDER'])
              .optional(),
            condition: z
              .object({
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'CALL_CENTER',
                    ])
                  )
                  .min(1)
                  .max(3),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('List of times when the discount is available.')
                  .optional(),
                minOrderPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                  })
                  .describe('Minimum order price for the discount.'),
                coupon: z
                  .object({
                    applied: z
                      .boolean()
                      .describe(
                        'Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.'
                      )
                      .optional()
                      .nullable(),
                    code: z
                      .string()
                      .describe('Coupon code.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Coupon associated with the discount.')
                  .optional(),
              })
              .describe(
                'Discount condition.\nAll conditions must be met so that a customer can apply the discount.'
              )
              .optional(),
          }),
          z.intersection(
            z.xor([
              z.object({
                percentage: z.never().optional(),
                amount: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                  })
                  .describe('Discount amount.'),
              }),
              z.object({
                amount: z.never().optional(),
                percentage: z.string().describe('Discount percentage.'),
              }),
            ]),
            z.xor([
              z.object({
                sectionIds: z.never().optional(),
                itemIds: z.never().optional(),
              }),
              z.object({
                itemIds: z.never().optional(),
                sectionIds: z
                  .object({ values: z.array(z.string()).optional() })
                  .describe('IDs of the sections the discount applies to.'),
              }),
              z.object({
                sectionIds: z.never().optional(),
                itemIds: z
                  .object({ values: z.array(z.string()).optional() })
                  .describe(
                    'IDs of the items the discount applies to. **Note:** The items must be of type `dish`.'
                  ),
              }),
            ])
          )
        )
        .describe('Discount to create.')
        .optional(),
    })
    .optional(),
});
export const CreateDiscountResponse = z.intersection(
  z.object({
    _id: z.string().describe('Discount ID.').optional().nullable(),
    name: z.string().describe('Discount name.').optional().nullable(),
    description: z
      .string()
      .describe('Discount description.')
      .optional()
      .nullable(),
    active: z
      .boolean()
      .describe('Whether the discount is active. Defaults to `true`.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNSPECIFIED_TYPE', 'OFF_ITEM', 'OFF_ORDER'])
      .describe('Discount type.')
      .optional(),
    condition: z
      .object({
        fulfillmentTypes: z
          .array(
            z.enum([
              'UNSPECIFIED_FULFILLMENT_TYPE',
              'DELIVERY',
              'PICKUP_OR_DINE_IN',
            ])
          )
          .min(1)
          .max(2)
          .optional(),
        platforms: z
          .array(
            z.enum([
              'UNSPECIFIED_PLATFORM',
              'SITE',
              'MOBILE_SITE',
              'CALL_CENTER',
            ])
          )
          .min(1)
          .max(3)
          .optional(),
        availability: z
          .object({
            periods: z
              .array(
                z.object({
                  openDay: z
                    .enum([
                      'UNDEFINED',
                      'SUN',
                      'MON',
                      'TUE',
                      'WED',
                      'THU',
                      'FRI',
                      'SAT',
                    ])
                    .describe('Day of the week the period starts on.')
                    .optional(),
                  openTime: z
                    .string()
                    .describe(
                      'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                    )
                    .optional(),
                  closeDay: z
                    .enum([
                      'UNDEFINED',
                      'SUN',
                      'MON',
                      'TUE',
                      'WED',
                      'THU',
                      'FRI',
                      'SAT',
                    ])
                    .describe('Day of the week the period ends on.')
                    .optional(),
                  closeTime: z
                    .string()
                    .describe(
                      'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                    )
                    .optional(),
                })
              )
              .optional(),
            specialHourPeriods: z
              .array(
                z.object({
                  startDate: z
                    .string()
                    .describe(
                      'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  endDate: z
                    .string()
                    .describe(
                      'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  available: z
                    .boolean()
                    .describe(
                      'Whether the item is available during the exception. Defaults to `true`.'
                    )
                    .optional(),
                  eventName: z
                    .string()
                    .describe(
                      'Name of the special hour period. In the dashboard, the special hour period is called event.'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
          })
          .describe('List of times when the discount is available.')
          .optional(),
        minOrderPrice: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
          })
          .describe('Minimum order price for the discount.')
          .optional(),
        coupon: z
          .object({
            applied: z
              .boolean()
              .describe(
                'Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.'
              )
              .optional()
              .nullable(),
            code: z.string().describe('Coupon code.').optional().nullable(),
          })
          .describe('Coupon associated with the discount.')
          .optional(),
      })
      .describe(
        'Discount condition.\nAll conditions must be met so that a customer can apply the discount.'
      )
      .optional(),
  }),
  z.intersection(
    z.xor([
      z.object({
        amount: z.never().optional(),
        percentage: z.never().optional(),
      }),
      z.object({
        percentage: z.never().optional(),
        amount: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
          })
          .describe('Discount amount.'),
      }),
      z.object({
        amount: z.never().optional(),
        percentage: z.string().describe('Discount percentage.'),
      }),
    ]),
    z.xor([
      z.object({
        sectionIds: z.never().optional(),
        itemIds: z.never().optional(),
      }),
      z.object({
        itemIds: z.never().optional(),
        sectionIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe('IDs of the sections the discount applies to.'),
      }),
      z.object({
        sectionIds: z.never().optional(),
        itemIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe(
            'IDs of the items the discount applies to. **Note:** The items must be of type `dish`.'
          ),
      }),
    ])
  )
);
export const ListDiscountsRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the discounts belong to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fieldMask: z
        .object({ paths: z.array(z.string()) })
        .describe('Filed mask path.')
        .optional()
        .nullable(),
      active: z
        .boolean()
        .describe(
          'Whether only active discounts are returned. Defaults to `true`.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListDiscountsResponse = z.object({
  discounts: z
    .array(
      z.intersection(
        z.object({
          _id: z.string().describe('Discount ID.').optional().nullable(),
          name: z.string().describe('Discount name.').optional().nullable(),
          description: z
            .string()
            .describe('Discount description.')
            .optional()
            .nullable(),
          active: z
            .boolean()
            .describe('Whether the discount is active. Defaults to `true`.')
            .optional()
            .nullable(),
          type: z
            .enum(['UNSPECIFIED_TYPE', 'OFF_ITEM', 'OFF_ORDER'])
            .describe('Discount type.')
            .optional(),
          condition: z
            .object({
              fulfillmentTypes: z
                .array(
                  z.enum([
                    'UNSPECIFIED_FULFILLMENT_TYPE',
                    'DELIVERY',
                    'PICKUP_OR_DINE_IN',
                  ])
                )
                .min(1)
                .max(2)
                .optional(),
              platforms: z
                .array(
                  z.enum([
                    'UNSPECIFIED_PLATFORM',
                    'SITE',
                    'MOBILE_SITE',
                    'CALL_CENTER',
                  ])
                )
                .min(1)
                .max(3)
                .optional(),
              availability: z
                .object({
                  periods: z
                    .array(
                      z.object({
                        openDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .describe('Day of the week the period starts on.')
                          .optional(),
                        openTime: z
                          .string()
                          .describe(
                            'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                          )
                          .optional(),
                        closeDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .describe('Day of the week the period ends on.')
                          .optional(),
                        closeTime: z
                          .string()
                          .describe(
                            'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  specialHourPeriods: z
                    .array(
                      z.object({
                        startDate: z
                          .string()
                          .describe(
                            'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        endDate: z
                          .string()
                          .describe(
                            'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        available: z
                          .boolean()
                          .describe(
                            'Whether the item is available during the exception. Defaults to `true`.'
                          )
                          .optional(),
                        eventName: z
                          .string()
                          .describe(
                            'Name of the special hour period. In the dashboard, the special hour period is called event.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .optional(),
                })
                .describe('List of times when the discount is available.')
                .optional(),
              minOrderPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                })
                .describe('Minimum order price for the discount.')
                .optional(),
              coupon: z
                .object({
                  applied: z
                    .boolean()
                    .describe(
                      'Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.'
                    )
                    .optional()
                    .nullable(),
                  code: z
                    .string()
                    .describe('Coupon code.')
                    .optional()
                    .nullable(),
                })
                .describe('Coupon associated with the discount.')
                .optional(),
            })
            .describe(
              'Discount condition.\nAll conditions must be met so that a customer can apply the discount.'
            )
            .optional(),
        }),
        z.intersection(
          z.xor([
            z.object({
              amount: z.never().optional(),
              percentage: z.never().optional(),
            }),
            z.object({
              percentage: z.never().optional(),
              amount: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                })
                .describe('Discount amount.'),
            }),
            z.object({
              amount: z.never().optional(),
              percentage: z.string().describe('Discount percentage.'),
            }),
          ]),
          z.xor([
            z.object({
              sectionIds: z.never().optional(),
              itemIds: z.never().optional(),
            }),
            z.object({
              itemIds: z.never().optional(),
              sectionIds: z
                .object({ values: z.array(z.string()).optional() })
                .describe('IDs of the sections the discount applies to.'),
            }),
            z.object({
              sectionIds: z.never().optional(),
              itemIds: z
                .object({ values: z.array(z.string()).optional() })
                .describe(
                  'IDs of the items the discount applies to. **Note:** The items must be of type `dish`.'
                ),
            }),
          ])
        )
      )
    )
    .optional(),
});
